import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Chart from "chart.js/auto";
import { Grid, Segment, Header } from "semantic-ui-react";

const AttendanceCharts = ({
  dailyCounts,
  weeklyCounts,
  monthlyCounts,
  hourlyStats,
}) => {
  const dailyChartRef = useRef(null);
  const weeklyChartRef = useRef(null);
  const monthlyChartRef = useRef(null);
  const hourlyChartRef = useRef(null);

  const chartInstances = useRef({});

  useEffect(() => {
    // Cleanup previous chart instances to prevent growth
    Object.values(chartInstances.current).forEach((chart) => chart.destroy());

    // Generate full list of 24 hours in 00:00 format
    const hourlyLabels = Array.from(
      { length: 24 },
      (_, i) => `${i.toString().padStart(2, "0")}:00`
    );

    const hourlyData = hourlyLabels.map((time) => hourlyStats[time] || 0);

    chartInstances.current.daily = new Chart(dailyChartRef.current, {
      type: "bar",
      data: {
        labels: Object.keys(dailyCounts),
        datasets: [
          {
            label: "Daily Attendance",
            data: Object.values(dailyCounts),
            backgroundColor: "rgba(54, 162, 235, 0.6)",
          },
        ],
      },
    });

    chartInstances.current.weekly = new Chart(weeklyChartRef.current, {
      type: "bar",
      data: {
        labels: Object.keys(weeklyCounts),
        datasets: [
          {
            label: "Weekly Attendance",
            data: Object.values(weeklyCounts),
            backgroundColor: "rgba(255, 206, 86, 0.6)",
          },
        ],
      },
    });

    chartInstances.current.monthly = new Chart(monthlyChartRef.current, {
      type: "bar",
      data: {
        labels: Object.keys(monthlyCounts),
        datasets: [
          {
            label: "Monthly Attendance",
            data: Object.values(monthlyCounts),
            backgroundColor: "rgba(75, 192, 192, 0.6)",
          },
        ],
      },
    });

    chartInstances.current.hourly = new Chart(hourlyChartRef.current, {
      type: "line",
      data: {
        labels: hourlyLabels,
        datasets: [
          {
            label: "Hourly Attendance",
            data: hourlyData,
            borderColor: "rgba(255, 99, 132, 1)",
            fill: false,
          },
        ],
      },
      options: {
        scales: {
          x: {
            title: {
              display: true,
              text: "Hour of Day",
            },
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: "Attendance Count",
            },
          },
        },
      },
    });

    // Capture the current charts for cleanup
    const chartsToCleanup = chartInstances.current;

    return () => {
      Object.values(chartsToCleanup).forEach((chart) => chart.destroy());
    };
  }, [dailyCounts, weeklyCounts, monthlyCounts, hourlyStats]);

  return (
    <Grid stackable columns={2}>
      <Grid.Row>
        <Grid.Column>
          <Segment>
            <Header as="h3">Daily Attendance</Header>
            <canvas ref={dailyChartRef} />
          </Segment>
        </Grid.Column>
        <Grid.Column>
          <Segment>
            <Header as="h3">Weekly Attendance</Header>
            <canvas ref={weeklyChartRef} />
          </Segment>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column>
          <Segment>
            <Header as="h3">Monthly Attendance</Header>
            <canvas ref={monthlyChartRef} />
          </Segment>
        </Grid.Column>
        <Grid.Column>
          <Segment>
            <Header as="h3">Hourly Attendance</Header>
            <canvas ref={hourlyChartRef} />
          </Segment>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

AttendanceCharts.propTypes = {
  dailyCounts: PropTypes.object.isRequired,
  weeklyCounts: PropTypes.object.isRequired,
  monthlyCounts: PropTypes.object.isRequired,
  hourlyStats: PropTypes.object.isRequired,
};

export default AttendanceCharts;
